/**
 * ModelEffortFields — the phase-9 §C/§D model + effort assignment controls,
 * shared by AgentSheet (agent defaults), ScheduleEditor (per-schedule
 * override), and the Activity run-now composer (per-job override). Compact
 * selects, default "inherit" (empty string = field omitted on the wire →
 * override > agent default > Claude Code's own default). The model select
 * carries a "custom…" branch for raw model ids (contract §C allows
 * /^[a-z0-9.-]{3,64}$/ for advanced users) — callers validate with
 * isValidModel() at save time.
 */
import { useState } from "react";
import { EFFORTS, MODEL_PRESETS, type Effort } from "../lib/api";

const CUSTOM = "__custom";

export function ModelEffortFields({
  model,
  effort,
  onModel,
  onEffort,
  modelLabel = "Model",
  effortLabel = "Effort",
  hint,
}: {
  /** "" = inherit. */
  model: string;
  /** "" = inherit. */
  effort: "" | Effort;
  onModel: (model: string) => void;
  onEffort: (effort: "" | Effort) => void;
  modelLabel?: string;
  effortLabel?: string;
  /** Plain-language line under the selects (caller-specific). */
  hint?: string;
}) {
  const isPreset = (MODEL_PRESETS as readonly string[]).includes(model);
  // "custom…" stays selected while its text input is empty/being typed.
  const [customOpen, setCustomOpen] = useState(model !== "" && !isPreset);
  const selectValue = customOpen ? CUSTOM : isPreset ? model : "";

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}>
          <span className="dsh-field-label">{modelLabel}</span>
          <select
            className="input"
            value={selectValue}
            onChange={(e) => {
              if (e.target.value === CUSTOM) {
                setCustomOpen(true);
                if (isPreset) onModel("");
              } else {
                setCustomOpen(false);
                onModel(e.target.value);
              }
            }}
          >
            <option value="">inherit</option>
            {MODEL_PRESETS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
            <option value={CUSTOM}>custom…</option>
          </select>
        </div>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}>
          <span className="dsh-field-label">{effortLabel}</span>
          <select
            className="input"
            value={effort}
            onChange={(e) => onEffort(e.target.value as "" | Effort)}
          >
            <option value="">inherit</option>
            {EFFORTS.map((ef) => (
              <option key={ef} value={ef}>
                {ef}
              </option>
            ))}
          </select>
        </div>
      </div>
      {customOpen && (
        <input
          className="input mono"
          style={{ marginTop: 8 }}
          placeholder="raw model id, e.g. claude-sonnet-4-5"
          value={model}
          onChange={(e) => onModel(e.target.value.trim())}
          aria-label="custom model id"
          spellCheck={false}
        />
      )}
      {hint && (
        <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
          {hint}
        </p>
      )}
    </div>
  );
}
